import { Component, OnInit, ViewChild } from '@angular/core';

import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { MatDialogRef } from '@angular/material/dialog';

import { TranslocoService } from '@jsverse/transloco';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { PasswordRulesCheckComponent } from 'app/modules/account/components/password-rules-check/password-rules-check.component';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { noWhitespaceValidator, passwordsMatchValidator } from 'app/utils/customValidators';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';

import { Group, GroupService } from '../../../../providers/semantria-api/group.service';
import {
  SemantriaUser,
  SemantriaUsersService,
} from '../../../../providers/semantria-api/semantria-users.service';
import { AlertService } from '../../../../services/alert.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-user-create-dialog',
  templateUrl: './user-create-dialog.component.html',
  styleUrls: ['./user-create-dialog.component.scss'],
  standalone: false,
})
export class UserCreateDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  userForm: UntypedFormGroup;

  isSaving: boolean;

  currentGroup: Group;

  @ViewChild(PasswordRulesCheckComponent)
  passwordRulesCheckComponent: PasswordRulesCheckComponent;
  userCreateStatuses = {
    pw: <ErrorObject>{ state: EnumErrorStates.SUCCESS },
    pwConfirm: <ErrorObject>{ state: EnumErrorStates.SUCCESS },
    firstName: <ErrorObject>{ state: EnumErrorStates.SUCCESS },
    lastName: <ErrorObject>{ state: EnumErrorStates.SUCCESS },
  };

  constructor(
    private userService: SemantriaUsersService,
    private alertService: AlertService,
    private groupService: GroupService,
    private dialogRef: MatDialogRef<UserCreateDialogComponent>,
    private fb: UntypedFormBuilder,
    private translocoService: TranslocoService
  ) {
    const validPasswordValidator = (): ValidatorFn => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (!this.passwordRulesCheckComponent) {
          return null;
        }
        return this.passwordRulesCheckComponent.isValid(control.value) ? null : { valid: true };
      };
    };

    this.userForm = this.fb.group(
      {
        email: ['', { validators: [Validators.required, Validators.email], updateOn: 'blur' }],
        alternateEmail: ['', { validators: [Validators.email], updateOn: 'blur' }],
        password: [
          '',
          {
            validators: [Validators.required, validPasswordValidator()],
            updateOn: 'change', // would rather have blur here but then the value changes don't get sent down to the password rule check component
          },
        ],
        passwordConfirm: ['', [Validators.required]],
        firstName: ['', [Validators.required, noWhitespaceValidator]],
        lastName: ['', [Validators.required, noWhitespaceValidator]],
        isAdmin: [false],
      },
      { validators: passwordsMatchValidator }
    );
  }

  ngOnInit() {}

  cancel() {
    this.dialogRef.close();
  }

  create() {
    if (this.userForm.valid) {
      this.isSaving = true;
      this.userForm.disable();

      this.currentGroup = this.groupService.getSelectedGroup();
      const user: SemantriaUser = {
        user_id: undefined,
        login: this.userForm.value.email,
        account_id: undefined,
        password: this.userForm.value.password,
        email: this.userForm.value.email,
        alternate_email: this.userForm.value.alternateEmail,
        first_name: this.userForm.value.firstName,
        last_name: this.userForm.value.lastName,
        company_name: undefined,
        user_groups: [this.currentGroup.group_id],
        permissions: [],
      };

      if (this.userForm.value.isAdmin) {
        user.permissions.push({ permission_type: 'ACCOUNT_ADMIN' });
        user.user_groups.splice(0, 1);
      }

      this.userService
        .createUser(user)
        .then((res) => {
          this.alertService.showToast(
            this.translocoService.translate('user-create-dialog.user-create', {
              email: user.email,
            })
          );
          this.dialogRef.close(res);
        })
        .catch((err) => {
          this.isSaving = false;
          this.userForm.enable();

          this.alertService.showErrorToast(
            this.translocoService.translate('user-create-dialog.user-create-failed', {
              email: user.email,
            }),
            err
          );
        });
    }
  }

  get password() {
    return this.userForm.controls.password;
  }

  get passwordConfirm() {
    return this.userForm.controls.passwordConfirm;
  }

  get email() {
    return this.userForm.controls.email;
  }

  get alternateEmail() {
    return this.userForm.controls.alternateEmail;
  }

  get firstName() {
    return this.userForm.controls.firstName;
  }

  get lastName() {
    return this.userForm.controls.lastName;
  }

  onInputChange(input: string) {
    const passwordErrorStringForKey = (key) => {
      switch (key) {
        case 'valid':
          return this.translocoService.translate('user-create-dialog.password-complexity');
        case 'required':
          return this.translocoService.translate('user-create-dialog.required-password');
        case 'passwordsNotMatch':
          return this.translocoService.translate('user-create-dialog.non-matched-passwords');
        default:
          return '';
      }
    };

    switch (input) {
      case 'pw':
      case 'pwConfirm':
        if (this.password.valid) {
          this.userCreateStatuses.pw = ErrorObjectUtils.create(EnumErrorStates.SUCCESS);
        }
        if (this.passwordConfirm.valid) {
          this.userCreateStatuses.pwConfirm = ErrorObjectUtils.create(EnumErrorStates.SUCCESS);
        }

        if (this.password.invalid && this.password.dirty) {
          const errorKey = Object.keys(this.password.errors)[0];
          this.userCreateStatuses.pw = ErrorObjectUtils.create(
            EnumErrorStates.ERROR,
            passwordErrorStringForKey(errorKey)
          );
        }

        if (this.passwordConfirm.invalid && this.passwordConfirm.dirty) {
          const errorKey = Object.keys(this.passwordConfirm.errors)[0];
          this.userCreateStatuses.pwConfirm = ErrorObjectUtils.create(
            EnumErrorStates.ERROR,
            passwordErrorStringForKey(errorKey)
          );
        }
        break;
      case 'firstName':
        if (this.firstName.hasError('required') || this.firstName.hasError('whitespace')) {
          this.userCreateStatuses.firstName = ErrorObjectUtils.create(
            EnumErrorStates.ERROR,
            this.translocoService.translate('user-create-dialog.given-name-req')
          );
        } else {
          this.userCreateStatuses.firstName = ErrorObjectUtils.create(EnumErrorStates.SUCCESS);
        }
        break;
      case 'lastName':
        if (this.lastName.hasError('required') || this.lastName.hasError('whitespace')) {
          this.userCreateStatuses.lastName = ErrorObjectUtils.create(
            EnumErrorStates.ERROR,
            this.translocoService.translate('user-create-dialog.family-name-req')
          );
        } else {
          this.userCreateStatuses.lastName = ErrorObjectUtils.create(EnumErrorStates.SUCCESS);
        }
        break;
    }
  }
}
